import { createFileRoute, Link } from "@tanstack/react-router";
import { hreflangLinks } from "@/lib/seo";
import { enSocialMeta, EN_CONTACT_EMAIL } from "@/lib/en-meta";
import { pageTitle } from "@/lib/brand";

const CANONICAL = "https://smartsustainability.ai/privacy-policy";
const TITLE = pageTitle("Privacy Policy", "en");
const DESCRIPTION =
  "Privacy policy for Smart Sustainability: what data is collected, how it is stored, and your rights under GDPR and KVKK.";

export const Route = createFileRoute("/privacy-policy")({
  head: () => ({
    meta: [
      { title: TITLE },
      ...enSocialMeta({ title: TITLE, description: DESCRIPTION, url: CANONICAL, type: "article" }),
      { name: "robots", content: "index,follow" },
    ],
    links: [
      { rel: "canonical", href: CANONICAL },
      ...hreflangLinks("/privacy", "/privacy-policy"),
    ],
  }),
  component: PrivacyEn,
});

function PrivacyEn() {
  return (
    <article className="max-w-3xl mx-auto px-6 py-20 prose-custom">
      <h1 className="font-serif text-4xl md:text-5xl mb-3">Privacy Policy</h1>
      <p className="text-sm text-muted-foreground mb-12">Last updated: May 20, 2026</p>

      <Section title="1. Data controller">
        This site (akillisurdurulebilirlik.com) is operated by Arda Öztaşkın as
        an independent publication and personal archive. Under the EU GDPR and
        Turkey's KVKK, the data controller is Arda Öztaşkın.
      </Section>

      <Section title="2. What is collected">
        <ul className="list-disc pl-6 space-y-2">
          <li>
            Newsletter signup: your email address and preferred language (EN/TR).
            Collected only when you submit the newsletter form yourself.
          </li>
          <li>
            Cookies and tracking: this site uses no advertising cookies and no
            third-party analytics (Google Analytics, Facebook Pixel, etc.).
          </li>
          <li>
            Server logs: the hosting provider may keep short-term technical logs
            (IP, user-agent) needed to operate the service.
          </li>
        </ul>
      </Section>

      <Section title="3. How your data is used">
        Your email is used only to send you the monthly briefing of new essays.
        It is not shared, sold, or rented to third parties for marketing.
      </Section>

      <Section title="4. Storage and infrastructure">
        Subscriber data is stored on Lovable Cloud (Supabase-based) with
        row-level security policies. Only the site owner can read it. The site
        is hosted on Lovable; images are served via the WordPress CDN
        (i0.wp.com).
      </Section>

      <Section title="5. Your GDPR / KVKK rights">
        You have the right to access, correct, delete, restrict processing of,
        and request portability of your data. To unsubscribe or have your data
        deleted, email the address below; requests are acted on within a
        reasonable time.
      </Section>

      <Section title="6. Contact">
        <p>
          Email:{" "}
          <a href={`mailto:${EN_CONTACT_EMAIL}`} className="underline hover:text-moss">
            {EN_CONTACT_EMAIL}
          </a>
        </p>
      </Section>

      <div className="mt-16">
        <Link
          to="/"
          className="inline-flex items-center text-[11px] uppercase tracking-[0.2em] font-bold text-ink hover:text-moss transition-colors"
        >
          ← Home
        </Link>
      </div>
    </article>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-10">
      <h2 className="font-serif text-2xl mb-3">{title}</h2>
      <div className="text-[15px] leading-relaxed text-ink/85">{children}</div>
    </section>
  );
}
